import { FC, useEffect } from 'react';
import { Layout } from './Layout';
import { Chat } from './Chat';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSheetSelection } from '../contexts/sheetSelection';
import { useAccounts } from '../hooks/useAccounts';
import { useChats } from '@/hooks/useChats';

export const Home: FC = () => {
  const { chatId, accountId } = useParams<{ chatId?: string; accountId?: string }>();
  const [searchParams] = useSearchParams();
  const { selectedCategoryIds, setSelectedSheetId, setSelectedCategoryIds } = useSheetSelection();
  const navigate = useNavigate();
  const hasCategoryIdsParam = Boolean(searchParams.get('categoryIds'));
  const isCategoryMode = selectedCategoryIds.length > 0 || hasCategoryIdsParam;

  const { accounts, loading: accountsLoading } = useAccounts();
  const { chats, loading: chatsLoading } = useChats(accountId, { skipLoad: isCategoryMode });

  // If sheetId is in query params (from BrowseView company click), set it
  useEffect(() => {
    const sheetIdParam = searchParams.get('sheetId');
    if (sheetIdParam) {
      setSelectedSheetId(parseInt(sheetIdParam, 10));
      setSelectedCategoryIds([]);
    }
  }, [searchParams, setSelectedSheetId, setSelectedCategoryIds]);

  // If categoryIds is in query params (from BrowseView sector chat), set them
  useEffect(() => {
    const categoryIdsParam = searchParams.get('categoryIds');
    if (categoryIdsParam) {
      const ids = categoryIdsParam
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
      if (ids.length > 0) {
        setSelectedCategoryIds(ids);
        setSelectedSheetId(null);
      }
    }
  }, [searchParams, setSelectedCategoryIds, setSelectedSheetId]);

  useEffect(() => {
    if (accountsLoading) return;
    if (accountId) return;
    if (accounts.length === 0) return;
    navigate(`/account/${accounts[0].id}`, { replace: true });
  }, [accountId, accounts, accountsLoading, navigate]);

  useEffect(() => {
    if (!accountId || accountsLoading) return;
    const accountExists = accounts.some((a) => String(a.id) === accountId);
    if (!accountExists) {
      navigate('/', { replace: true });
    }
  }, [accountId, accountsLoading, accounts, navigate]);

  useEffect(() => {
    if (isCategoryMode) return;
    if (!accountId) {
      setSelectedSheetId(null);
      setSelectedCategoryIds([]);
      return;
    }

    if (!chatId || chatId === 'new') {
      return;
    }

    if (chatsLoading) return;

    if (Number.isNaN(Number(chatId))) {
      navigate(`/account/${accountId}`, { replace: true });
      return;
    }

    const chatExists = chats.some((c) => String(c.id) === chatId);
    if (!chatExists && chatId !== 'new') {
      navigate(`/account/${accountId}`, { replace: true });
      return;
    }
  }, [
    accountId,
    chatId,
    chatsLoading,
    chats,
    navigate,
    setSelectedSheetId,
    setSelectedCategoryIds,
    isCategoryMode,
  ]);

  const handleBack = () => {
    if (accountId) {
      navigate(`/account/${accountId}`);
    } else {
      navigate('/');
    }
  };

  return (
    <Layout onBack={handleBack}>
      <Chat />
    </Layout>
  );
};
